/**
 * The `next.config.ts` redirect table, ported (RO-3-14).
 *
 * ── The third source of redirect truth ──
 *
 * This repo redirects from THREE places, and porting any one of them alone is a
 * silent behaviour change:
 *
 *   1. `excellent/next.config.ts` → `redirects()` — 26 rules. THIS FILE.
 *   2. `excellent/app/layout.tsx` lines 161–344 — the eight-gate mode cascade,
 *      already ported by RO-0-05 to `client/shell/redirects.ts`.
 *   3. Redirect STUB PAGES — a `page.tsx` whose entire body is `redirect(...)`.
 *      42 of those exist; 27 are static paths that belong to whichever L3 item
 *      ports their page, but ONE is load-bearing here:
 *      `app/projects/[projectSlug]/tasks/page.tsx`. `next.config.ts` documents
 *      in a comment why that rule is a page and not a config entry (a wildcard
 *      `/projects/:slug/tasks` source would also shadow the live
 *      `/projects/api/tasks` endpoint), which makes it the 4th member of the
 *      `/tasks` → `/issues` rule family. Porting the three config rules and
 *      dropping the page would break `/projects/<p>/tasks` deep links, so it is
 *      included below as the last rule, flagged `pageStub: true`.
 *
 * ── ORDER IS BEHAVIOUR, and it is not the same order as the cascade ──
 *
 * In Next, `redirects()` runs at the ROUTING layer — before any filesystem route
 * resolves, so before the root layout renders and therefore before all eight
 * cascade gates. Reproducing that precedence is not a formality; it changes
 * destinations. Measured example, `/projects/<p>/development/board`:
 *
 *   config first  (Next's real order) → /projects/<p>/work?area=queue&sub=board
 *   cascade first (the wrong order)   → /projects/<p>/product?area=development&sub=board
 *
 * A different MODE. So `__root.tsx` calls `resolveConfigRedirect` BEFORE
 * `resolveShellRedirect`, and `test-route-tree-parity` pins that relative order
 * with the case above rather than trusting the call site to stay put.
 *
 * Within this table the rules are first-match-wins in `next.config.ts`'s own
 * source order — the file's own comments call that out twice ("Order matters: the
 * `roadmap` rules must precede the `:path*` wildcards"). Do not sort this array.
 *
 * ── Query strings ──
 *
 * Next forwards the incoming query to the destination automatically (the
 * `/agents/objectives` rule's comment relies on it: "the incoming `?focus=<uid>`
 * deep-link query is forwarded automatically"). `mergeQuery` below does that,
 * with the destination's OWN literal query winning a key collision — the same
 * precedence `modeRedirectUrl` already uses for the cascade
 * (`if (!(k in extra))`), so the two halves of the redirect story agree.
 */

/** One `next.config.ts` redirect rule. All 26 are `permanent: true`; the SPA has
 *  no 301/308 to emit, so permanence is not modelled — every hop is a
 *  `replace: true` client navigation, which is the closest honest analogue. */
interface ConfigRedirect {
  /** Next `source` pattern: literals, `:param` (one segment), `:name*` (0+ trailing). */
  source: string;
  /** Next `destination`, with the same tokens plus an optional literal `?query`. */
  destination: string;
  /** True for the one rule that lives in a stub PAGE, not in `next.config.ts`. */
  pageStub?: boolean;
  /**
   * Segment values a `:param` must REFUSE, because a static sibling route in
   * `excellent/app/**` owns them.
   *
   * This models the one thing Next's file router did for free and a flat
   * first-match table does not: a static segment outranks a dynamic one, so
   * `app/projects/api/tasks/route.ts` beats `app/projects/[projectSlug]/tasks`
   * and `/projects/api/tasks` reaches the live endpoint instead of redirecting.
   * `next.config.ts` explains that exact ranking in the comment justifying why
   * this rule is a page stub at all — and the flat port lost it, which
   * `test-route-tree-parity` caught before this shipped.
   *
   * Only declared where a real collision exists. `/projects/map/tasks` is NOT
   * excluded: `app/projects/map/` has no `tasks` leaf, so Next falls through to
   * the dynamic segment there and so does this table.
   */
  staticSiblings?: Readonly<Record<string, readonly string[]>>;
}

/**
 * The table, in `next.config.ts` source order. Line references are to that file
 * so a reviewer can diff the two side by side.
 *
 * A `:name*` token only ever appears LAST in a source — `matchRule` consumes the
 * remainder when it sees one and stops, which is exactly Next's semantics for a
 * trailing catch-all and the only position Next's own rules use.
 */
export const CONFIG_REDIRECTS: readonly ConfigRedirect[] = [
  // Objectives re-homed out of Agents (Measured Business F3) — next.config.ts:144.
  { source: "/agents/objectives", destination: "/work?area=objectives" },

  // Board is the Issues ▸ Board sub-view — next.config.ts:155-166. Pointed
  // straight at `?sub=board` because a `?view=board` on the mode route is
  // stripped by the modes resolver, so the old two-hop form landed on Tasks.
  { source: "/board", destination: "/work?area=queue&sub=board" },
  { source: "/projects/:slug/board", destination: "/projects/:slug/work?area=queue&sub=board" },
  // NB the destination drops `:sectionSlug` — the board is a WORK-tier surface,
  // so a section-scoped board URL escapes its section. Faithful to the source.
  {
    source: "/projects/:projectSlug/:sectionSlug/board",
    destination: "/projects/:projectSlug/work?area=queue&sub=board",
  },

  // Issues tracker renamed from /tasks (#533) — next.config.ts:173-183.
  { source: "/tasks", destination: "/issues" },
  {
    source: "/projects/sections/:sectionSlug/tasks",
    destination: "/projects/sections/:sectionSlug/issues",
  },
  {
    source: "/projects/:projectSlug/:sectionSlug/tasks",
    destination: "/projects/:projectSlug/:sectionSlug/issues",
  },

  // Workflows + Goals aliases. LIVE-CLOSEOUT: leftover `/agents/goals` and
  // `/agents/roadmap` dests hop to `/` so they do not remount ProductChrome.
  // The `roadmap` rules MUST precede the `:path*` wildcards (first match wins).
  { source: "/workflows/roadmap", destination: "/" },
  // #46665: verification IA owns `/workflows` and `/workflows/$id` (HG12).
  // Do not fold those into `/agents/workflows`. `/loops` hops to `/`.
  { source: "/goals", destination: "/" },
  { source: "/goals/:path*", destination: "/" },

  // Loops aliases. LIVE-CLOSEOUT: leftover `/agents/workflows` dest hops to `/`.
  { source: "/loops/roadmap", destination: "/" },
  { source: "/loops", destination: "/" },
  { source: "/loops/:path*", destination: "/" },

  // Section-mounted Workflows + Goals → the section Agents view's sub-tabs —
  // next.config.ts:226-244. Exact-path rules (no `:path*`) for workflows/goals
  // because the section goals create/detail sub-routes stay live: only the INDEX
  // folds in. The Loops alias chains to the same place and DOES take `:path*`.
  {
    source: "/projects/:projectSlug/:sectionSlug/workflows",
    destination: "/projects/:projectSlug/:sectionSlug/agents?view=workflows",
  },
  {
    source: "/projects/:projectSlug/:sectionSlug/goals",
    destination: "/projects/:projectSlug/:sectionSlug/agents?view=goals",
  },
  {
    source: "/projects/:projectSlug/:sectionSlug/loops",
    destination: "/projects/:projectSlug/:sectionSlug/agents?view=workflows",
  },
  {
    source: "/projects/:projectSlug/:sectionSlug/loops/:path*",
    destination: "/projects/:projectSlug/:sectionSlug/agents?view=workflows",
  },

  // Lens / Efficiency — next.config.ts:254-291 sent these to Analytics "You".
  // That page is unmounted (#44575). LIVE-CLOSEOUT: leftover `/agents` dest
  // hops to `/` so bookmarks do not remount ProductChrome. The standalone
  // `/lens` + `/lens/*` paths stay in cascade gate 3 (it must skip `/lens/api/*`).
  {
    source: "/projects/:projectSlug/operations/lens",
    destination: "/",
  },
  {
    source: "/projects/:projectSlug/operations/lens/:path*",
    destination: "/",
  },
  { source: "/agents/lens", destination: "/" },
  { source: "/agents/lens/:path*", destination: "/" },
  { source: "/agents/efficiency", destination: "/" },
  { source: "/agents/efficiency/:path*", destination: "/" },
  // quality → fleet/quality was a hop into analytics; both leftover dests to `/`.
  { source: "/agents/analytics/quality", destination: "/" },

  // ── The page-stub rule (source 3 above) ────────────────────────────────────
  // `app/projects/[projectSlug]/tasks/page.tsx`. Last on purpose: in Next every
  // config rule resolves before any page renders, so a stub page is strictly
  // lower priority than the whole table above. No config rule matches a 3-segment
  // `/projects/<p>/tasks`, so today the position is not contended — it is ordered
  // this way so it stays correct if one ever is.
  {
    source: "/projects/:projectSlug/tasks",
    destination: "/projects/:projectSlug/issues",
    pageStub: true,
    // `app/projects/api/tasks/route.ts` is live. See `staticSiblings` above.
    staticSiblings: { projectSlug: ["api"] },
  },
];

type RuleParams = Record<string, string>;

/**
 * Match one `source` against already-split path segments, returning captures or
 * `null`. A `:name*` token captures every remaining segment (possibly NONE — Next
 * matches `/workflows` against `/workflows/:path*`, which is why the exact
 * `/workflows` rule has to sit above it) and terminates the walk.
 */
function matchRule(rule: ConfigRedirect, segments: string[]): RuleParams | null {
  const tokens = rule.source.split("/").filter(Boolean);
  const params: RuleParams = {};

  for (let i = 0; i < tokens.length; i++) {
    const token = tokens[i]!;
    if (token.startsWith(":") && token.endsWith("*")) {
      params[token.slice(1, -1)] = segments.slice(i).join("/");
      return params;
    }
    if (i >= segments.length) return null;
    if (token.startsWith(":")) {
      const name = token.slice(1);
      // A static sibling route owns this value — Next's router would have picked
      // it over the dynamic segment, so this rule must not match.
      if (rule.staticSiblings?.[name]?.includes(segments[i]!)) return null;
      params[name] = segments[i]!;
      continue;
    }
    if (token !== segments[i]) return null;
  }

  // No catch-all consumed the tail, so the lengths must match exactly: Next does
  // not treat `/tasks` as a prefix of `/tasks/anything`.
  return tokens.length === segments.length ? params : null;
}

/**
 * Substitute captures into a `destination`. An EMPTY catch-all capture collapses
 * to nothing rather than a trailing slash — `/loops` → `/`, not `//` — which
 * is what Next emits for an empty catch-all and what the route tree registers.
 */
function buildDestination(destination: string, params: RuleParams): string {
  const [rawPath = "", rawQuery = ""] = destination.split("?");
  const path = rawPath
    .split("/")
    .filter(Boolean)
    .map((token) => {
      if (token.startsWith(":") && token.endsWith("*")) return params[token.slice(1, -1)] ?? "";
      if (token.startsWith(":")) return params[token.slice(1)] ?? "";
      return token;
    })
    .filter(Boolean)
    .join("/");
  return `/${path}${rawQuery ? `?${rawQuery}` : ""}`;
}

/**
 * Forward the incoming query onto the destination. The destination's own literal
 * query wins a key collision (`/work?area=queue&sub=board` keeps its `sub` even
 * if the request carried one), matching `modeRedirectUrl`'s rule for the cascade.
 *
 * @param search `location.searchStr` — INCLUDING its leading `?`, the same shape
 *               `resolveShellRedirect` takes.
 */
function mergeQuery(destination: string, search: string): string {
  const incomingRaw = search.startsWith("?") ? search.slice(1) : search;
  if (!incomingRaw) return destination;

  const [path = "", destQuery = ""] = destination.split("?");
  const merged = new URLSearchParams(destQuery);
  for (const [key, value] of new URLSearchParams(incomingRaw)) {
    if (!merged.has(key)) merged.append(key, value);
  }
  const query = merged.toString();
  return query ? `${path}?${query}` : path;
}

/**
 * Resolve a path against the config redirect table, or `null` when no rule
 * matches. Pure — no DB, no `next/*`, no router. Must be called BEFORE
 * `resolveShellRedirect` (see the header for the measured reason).
 *
 * @param pathname `location.pathname`
 * @param search   `location.searchStr`, including its leading `?`
 */
export function resolveConfigRedirect(pathname: string, search: string): string | null {
  const segments = pathname.split("/").filter(Boolean);

  for (const rule of CONFIG_REDIRECTS) {
    const params = matchRule(rule, segments);
    if (!params) continue;
    const destination = mergeQuery(buildDestination(rule.destination, params), search);
    // A rule that resolves to where we already are is not a redirect, it is a
    // loop. None do today; the guard is the same one the cascade's `dest !==
    // pathname + search` line exists for, and costs one comparison.
    if (destination === pathname + search) return null;
    return destination;
  }

  return null;
}
